import { ThunderboltFilled } from '@ant-design/icons';
import { Divider, Form } from 'antd';
import { Fragment, useCallback, useEffect } from 'react';

import { DoubleThunderbolt } from 'app/components/commons/DoubleThunderbolt/DoubleThunderbolt';
import type { ExtranetPackage } from 'app/typings/packages';
import { isValidDiscount } from 'app/utils/price';
import { assertNever } from 'app/utils/typing';

import { RuleSection } from '../../../../../PricingRule/RuleSection';
import type { RuleKeys, RuleType } from '../../../../../PricingRule/Utils';
import type { FormValues, PackageRuleValues } from '../../../Utils';

import './Discount.scss';

type Props = {
  values: FormValues;
  setDiscountStepHasError: (val: boolean) => void;
  packages: ExtranetPackage[];
};

export const Discount = ({
  values,
  setDiscountStepHasError,
  packages,
}: Props) => {
  const form = Form.useFormInstance();

  const errorDiscount = useCallback(
    (packageId: number) => {
      const formIndex = values.packageRules.findIndex(
        (pr) => pr.packageId === packageId
      );

      const dayPackage = packages.find((p) => p.id === packageId)?.dayPackage;

      return (
        values.packageRules[formIndex].discountIsActivated &&
        values.packageRules[formIndex].discount !== undefined &&
        !isValidDiscount(
          -(values.packageRules[formIndex].discount ?? 0),
          !!dayPackage
        )
      );
    },
    [values, packages]
  );

  const errorLMDiscount = useCallback(
    (packageId: number) => {
      const formIndex = values.packageRules.findIndex(
        (pr) => pr.packageId === packageId
      );
      const dayPackage = packages.find((p) => p.id === packageId)?.dayPackage;

      return (
        values.packageRules[formIndex].lastMinuteIsActivated &&
        values.packageRules[formIndex].lastMinuteDiscountValue !== undefined &&
        !isValidDiscount(
          -(values.packageRules[formIndex].lastMinuteDiscountValue ?? 0),
          !!dayPackage
        )
      );
    },
    [values, packages]
  );

  const errorLMDays = useCallback(
    (packageId: number) => {
      const formIndex = values.packageRules.findIndex(
        (pr) => pr.packageId === packageId
      );

      return (
        values.packageRules[formIndex].lastMinuteIsActivated &&
        !values.packageRules[formIndex].lastMinuteDays
      );
    },
    [values]
  );

  useEffect(() => {
    const hasError = values.packageRules
      .map(
        (pr) =>
          (!pr.discountIsActivated && !pr.lastMinuteIsActivated) ||
          errorDiscount(pr.packageId) ||
          errorLMDays(pr.packageId) ||
          errorLMDiscount(pr.packageId)
      )
      .includes(true);

    setDiscountStepHasError(hasError);
  }, [
    errorDiscount,
    errorLMDays,
    errorLMDiscount,
    values,
    setDiscountStepHasError,
  ]);

  const toggleSwitch = (
    switchValue: boolean,
    section: string,
    packageId: number
  ) => {
    const formIndex = values.packageRules.findIndex(
      (pr) => pr.packageId === packageId
    );

    if (section === 'lastMinuteDiscount') {
      form.setFieldValue(
        ['packageRules', formIndex, 'lastMinuteDiscountValue'],
        undefined
      );
      form.setFieldValue(
        ['packageRules', formIndex, 'lastMinuteDays'],
        undefined
      );
      form.setFieldValue(
        ['packageRules', formIndex, 'lastMinuteIsActivated'],
        switchValue
      );
    } else {
      form.setFieldValue(['packageRules', formIndex, 'discount'], undefined);
      form.setFieldValue(
        ['packageRules', formIndex, 'discountIsActivated'],
        switchValue
      );
    }
  };

  const getIsRuleInvalid = (rule: RuleKeys, packageId: number): boolean => {
    switch (rule) {
      case 'standardDiscount':
        return errorDiscount(packageId);

      case 'lastMinuteDiscount':
        return errorLMDiscount(packageId);

      default:
        assertNever(rule);
    }
  };

  const getRuleFromForm = (
    ruleForm: PackageRuleValues,
    idx: number,
    key: 'standardDiscount' | 'lastMinuteDiscount'
  ): RuleType => ({
    key,
    title: ruleForm.packageName,
    pkgId: ruleForm.packageId,
    fieldName: ['packageRules', idx],
  });

  return (
    <div className="discount-step">
      <div className="explanation">
        Apply the discounts that will be visible on Staycation.
      </div>
      <div className="auto-inventory-pricing-rule">
        <div className="section-header">
          <div className="section-title">
            <div className="section-icon">
              <ThunderboltFilled />
            </div>
            <p className="title">What's your usual Staycation discount?</p>
          </div>
        </div>
        {values.packageRules.map((ruleForm, index) => {
          const rule = getRuleFromForm(ruleForm, index, 'standardDiscount');

          return (
            <Fragment key={`rule_discount${ruleForm.packageId}`}>
              <RuleSection
                ruleInfo={rule}
                isRuleActive={ruleForm.discountIsActivated}
                packages={packages}
                toggleSwitch={(switchValue: boolean) =>
                  toggleSwitch(switchValue, 'discount', ruleForm.packageId)
                }
                isSingleRate={true}
                isRuleInvalid={getIsRuleInvalid(rule.key, ruleForm.packageId)}
              />
            </Fragment>
          );
        })}
        <Divider className="divider" plain>
          <div className="divider-text">and / or</div>
        </Divider>
        <div className="section-header">
          <div className="section-title">
            <div className="section-icon">
              <DoubleThunderbolt />
            </div>
            <p className="title">
              Would you like a different last minute discount?
            </p>
          </div>
        </div>
        {values.packageRules.map((ruleForm, index) => {
          const rule = getRuleFromForm(ruleForm, index, 'lastMinuteDiscount');

          return (
            <Fragment key={`rule_discount${ruleForm.packageId}`}>
              <RuleSection
                ruleInfo={rule}
                isRuleActive={ruleForm.lastMinuteIsActivated}
                packages={packages}
                toggleSwitch={(switchValue: boolean) =>
                  toggleSwitch(
                    switchValue,
                    'lastMinuteDiscount',
                    ruleForm.packageId
                  )
                }
                isSingleRate={true}
                isRuleInvalid={getIsRuleInvalid(rule.key, ruleForm.packageId)}
              />
            </Fragment>
          );
        })}
      </div>
    </div>
  );
};
